import type { ChapterSummary } from '@listening/api-contract/schemas';
import type { FetchChaptersFnReturnType } from '@listening/api-contract/legacy-client-types';
import { useContextAndErrorIfNull } from '@listening/utils/react-helpers';
import { assertDefined, timeoutPromise } from '@listening/utils/ts-utils';
import { useLatestCell } from '@listening/frontend/hooks/use-latest-cell';
import { RegistryContext } from '@effect/atom-react';
import React, {
  type RefObject,
  use,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  buildWordSpanProps,
  type LoadingParagraphRenderProps,
  type ParagraphRenderProps,
  SentenceRender,
  WordRender,
  type WordRenderProps,
} from '@/components/start/read-along/read-along-renders';
import { useAudioChunkSelector } from '@/lib/audio-chunk-player';
import { scrollablePrefixSums } from '@/lib/doc-reader/chapter-cache';
import { useWordHighlightManager } from '@/lib/doc-reader/highlight/highlight-registry';
import { createWordElementRegistry } from '@/lib/doc-reader/highlight/word-element-registry';
import {
  CHAPTER_CHUNK_SIZE,
  type ChapterChunkQueryResult,
  type CommonScrollOptions,
  type DocReaderCommonProps,
  DocReaderContext,
  type DocReaderViewHandle,
  SCROLL_TO_UNLOADED_CHUNK_TIMEOUT_MS,
  useCore,
} from '@/lib/doc-reader/core';
import { loadLegacyChapterChunk } from '@/lib/doc-reader/legacy-chapter-chunks';

export type ChapterHeaderRenderProps = {
  chapterIdx: number;
  chapterSummary: ChapterSummary;
  queryResult: ChapterChunkQueryResult;
  scrollableIdx: number;
  type: 'chapter-header';
};

export type ReaderRenderOverrides = {
  ChapterHeaderRender?: React.ComponentType<ChapterHeaderRenderProps>;
  ParagraphRender?: React.ComponentType<
    ParagraphRenderProps & { ref: React.Ref<HTMLDivElement> }
  >;
  WordRender?: React.ComponentType<WordRenderProps>;
  forceWordRender?: boolean;
};

export type ReaderViewProps = {
  scrollContainerParentClassName?: string;
  headerEstimateSize?: number;
  paragraphEstimateSizePerWord?: number;
  renders?: ReaderRenderOverrides;
} & DocReaderCommonProps;

const noop = () => undefined;

const DEFAULT_HEADER_ESTIMATE_SIZE = 76;
const DEFAULT_PARAGRAPH_ESTIMATE_SIZE_PER_WORD = 3.2;
export const DEFAULT_READER_SCROLL_OPTIONS: Required<CommonScrollOptions> = {
  overscan: 8,
};

export const useReaderCommon = ({
  headerEstimateSize,
  paragraphEstimateSizePerWord,
  onIsActiveVisibleChange,
  renders,
}: {
  headerEstimateSize: ReaderViewProps['headerEstimateSize'];
  paragraphEstimateSizePerWord: ReaderViewProps['paragraphEstimateSizePerWord'];
  onIsActiveVisibleChange: ReaderViewProps['onIsActiveVisibleChange'];
  renders?: ReaderRenderOverrides | undefined;
}) => {
  const core = useCore({
    onIsActiveVisibleChange,
  });

  const scrollableObjects = useScrollableObjectsDynamic({
    queries: core.queries,
    paragraphEstimateSizePerWord,
    renders,
  });

  const { getScrollableItemProps } = scrollableObjects;
  const estimateSizeOfScrollableElement = useCallback(
    (scrollableIndex: number) => {
      const scrollableItem = getScrollableItemProps(scrollableIndex);
      if (scrollableItem.type === 'chapter-header')
        return headerEstimateSize ?? DEFAULT_HEADER_ESTIMATE_SIZE;
      return (
        scrollableItem.chapterSummary.avg_paragraph_word_length *
        (paragraphEstimateSizePerWord ??
          DEFAULT_PARAGRAPH_ESTIMATE_SIZE_PER_WORD)
      );
    },
    [getScrollableItemProps, headerEstimateSize, paragraphEstimateSizePerWord],
  );

  const { debouncedUpdateViewedChapters } = core;
  const virtualItemsChanged = useCallback(
    (minVirtualIndex: number, maxVirtualIndex: number) => {
      if (maxVirtualIndex < minVirtualIndex)
        throw new Error('Invalid virtual range');
      if (minVirtualIndex < 0) return;
      if (maxVirtualIndex < 0) return;
      const firstVisibleChapterIdx =
        getScrollableItemProps(minVirtualIndex).chapterIdx;
      const lastVisibleChapterIdx =
        getScrollableItemProps(maxVirtualIndex).chapterIdx;
      const visibleChapterIndices = Array.from(
        { length: lastVisibleChapterIdx - firstVisibleChapterIdx + 1 },
        (_, i) => firstVisibleChapterIdx + i,
      );
      debouncedUpdateViewedChapters.maybeExecute(visibleChapterIndices);
    },
    [debouncedUpdateViewedChapters, getScrollableItemProps],
  );
  // Active paragraph element for the follow adapter — a cell, not state: the
  // paragraph ref callback writes it and the adapter reads it directly, so a
  // paragraph advance never re-renders the view.
  const activeParagraphElement = useLatestCell<HTMLParagraphElement | null>(
    null,
  );
  const setActiveParagraphElement = useCallback(
    (el: HTMLParagraphElement | null) => {
      activeParagraphElement.set(el);
    },
    [activeParagraphElement],
  );

  // DOM-driven word highlighting for the text view. The follow anchor here is
  // the active paragraph element (set by the paragraph ref callback), so the
  // manager's own anchor is ignored — it only toggles `data-active` on words.
  const { skipWordHighlighting } = useContextAndErrorIfNull(DocReaderContext);
  const [registry] = useState(() => createWordElementRegistry<HTMLElement>());
  useWordHighlightManager({
    registry,
    // The text view's follow anchor is the active paragraph element, so the
    // manager's own anchor is intentionally ignored here.
    onAnchorElementChange: noop,
    enabled: !skipWordHighlighting,
  });

  return {
    scrollableObjects,
    estimateSizeOfScrollableElement,
    virtualItemsChanged,
    activeParagraphElement,
    setActiveParagraphElement,
    highlightRegistry: registry,
    core,
  };
};

// There was another implementation of this that pre-computes all of the scrollableItems into a single array.
// Doing that means you always construct the active paragraph which could have many children regardless of whether it is visible.
const useScrollableObjectsDynamic = ({
  queries,
  paragraphEstimateSizePerWord,
  renders,
}: {
  queries: ReadonlyArray<ChapterChunkQueryResult>;
  paragraphEstimateSizePerWord: ReaderViewProps['paragraphEstimateSizePerWord'];
  renders?: ReaderRenderOverrides | undefined;
}) => {
  const { documentSummary, paragraphCounts } =
    useContextAndErrorIfNull(DocReaderContext);
  const ttsId = useAudioChunkSelector((s) => s.ttsId);

  const documentChapterSummaries = documentSummary.chapters;
  // Each number in this array is the cumulative sum of the number of paragraphs in the chapters before it.
  const paragraphCountsPrefixSums = useMemo(
    () => scrollablePrefixSums(paragraphCounts),
    [paragraphCounts],
  );

  const getChapterIdxByScrollableIndex = useCallback(
    (index: number): number => {
      let low = 0;
      let high = paragraphCountsPrefixSums.length - 1;
      while (low < high) {
        const mid = Math.floor((low + high) / 2);
        if (assertDefined(paragraphCountsPrefixSums[mid + 1]) <= index) {
          low = mid + 1;
        } else {
          high = mid;
        }
      }
      return low;
    },
    [paragraphCountsPrefixSums],
  );

  const scrollableItemsCount = paragraphCounts.reduce(
    (acc, paragraphCount) => acc + paragraphCount + 1,
    0,
  );

  const getScrollableItemProps = useCallback(
    (
      index: number,
    ):
      | ChapterHeaderRenderProps
      | ParagraphRenderProps
      | LoadingParagraphRenderProps => {
      const chapterIdx = getChapterIdxByScrollableIndex(index);
      const offset = assertDefined(paragraphCountsPrefixSums[chapterIdx]); // number of items before chapterIdx
      const chapterSummary = assertDefined(
        documentChapterSummaries[chapterIdx],
      );
      const chunkIdx = Math.floor(chapterIdx / CHAPTER_CHUNK_SIZE);
      const chapterChunkQuery = assertDefined(queries[chunkIdx]);

      if (index === offset) {
        return {
          type: 'chapter-header' as const,
          chapterIdx,
          queryResult: chapterChunkQuery,
          chapterSummary,
          scrollableIdx: index,
        };
      }

      const paragraphIdx = index - offset - 1; // because offset was chapter header
      const chapterData =
        chapterChunkQuery.data?.data[chapterIdx % CHAPTER_CHUNK_SIZE];
      // The index space comes from the summary's `num_paragraphs` until a chunk
      // teaches it otherwise, and the two are counted from different columns on
      // the server. A row past the end of the loaded chapter loads rather than
      // throws.
      const paragraph = chapterData?.paragraphs[paragraphIdx];

      if (chapterData && paragraph) {
        let firstActive = false;
        let lastActive = false;
        let isActive = false;
        // const activeParagraph = paragraph.sentences.some((s) =>
        //   s.words.some(
        //     (w) =>
        //       !!currentAudioState.ttsId &&
        //       w.chunkId === currentAudioState.ttsId,
        //   ),
        // );
        for (const [sIdx, sentence] of paragraph.sentences.entries()) {
          for (const [wIdx, word] of sentence.words.entries()) {
            if (word.chunkId === ttsId) {
              isActive = true;
              const prevWord =
                sentence.words[wIdx - 1] ??
                paragraph.sentences[sIdx - 1]?.words.at(-1) ??
                chapterData.paragraphs[paragraphIdx - 1]?.sentences
                  .at(-1)
                  ?.words.at(-1);
              firstActive = prevWord?.chunkId !== ttsId;
              const nextWord =
                sentence.words[wIdx + 1] ??
                paragraph.sentences[sIdx + 1]?.words[0] ??
                chapterData.paragraphs[paragraphIdx + 1]?.sentences[0]
                  ?.words[0];
              lastActive = nextWord?.chunkId !== ttsId;
              break;
            }
          }
          if (isActive) {
            break;
          }
        }
        return {
          type: 'paragraph',
          paragraphIdx,
          paragraph,
          chapterIdx,
          chapterSummary,
          scrollableIdx: index,
          chapterId: chapterSummary.id,
          isActive,
          firstActive,
          lastActive,
          children:
            isActive || renders?.forceWordRender
              ? paragraph.sentences.map((sentence, sentenceIdx) => (
                  <SentenceRender
                    key={`c_${chapterIdx.toString()}_p_${paragraphIdx.toString()}_s_${sentenceIdx.toString()}`}
                    sentence={sentence}
                  >
                    {sentence.words.map((word, wordIdx) => {
                      const Word = renders?.WordRender ?? WordRender;
                      // Same element type at a stable key (no remount storm);
                      // active styling is applied via `data-active` by the
                      // highlight manager, not a per-word React render.
                      return (
                        <Word
                          key={`c_${chapterIdx.toString()}_p_${paragraphIdx.toString()}_s_${sentenceIdx.toString()}_w_${wordIdx.toString()}`}
                          word={word}
                          spanProps={buildWordSpanProps(word, {
                            chapterId: chapterSummary.id,
                            paragraphIdx,
                            sentenceIdx,
                          })}
                        />
                      );
                    })}
                  </SentenceRender>
                ))
              : paragraph.sentences
                  .map((sentence) =>
                    sentence.words.map((word) => word.text).join(' '),
                  )
                  .join(' '),
        };
      }
      return {
        type: 'loading-paragraph',
        chapterIdx,
        chapterSummary,
        paragraphIdx,
        scrollableIdx: index,
        estimatedHeight:
          (paragraphEstimateSizePerWord ??
            DEFAULT_PARAGRAPH_ESTIMATE_SIZE_PER_WORD) *
          chapterSummary.avg_paragraph_word_length,
        queryResult: chapterChunkQuery,
      };
    },
    [
      documentChapterSummaries,
      getChapterIdxByScrollableIndex,
      paragraphCountsPrefixSums,
      paragraphEstimateSizePerWord,
      queries,
      renders,
      ttsId,
    ],
  );

  const findScrollableIndexOfTtsChunkId = useCallback(
    (ttsId: string, chapterChunkData?: FetchChaptersFnReturnType) => {
      const chapterIdx = documentChapterSummaries.findIndex((summary) =>
        summary.tts_ids.includes(ttsId),
      );
      if (chapterIdx === -1) return null;

      const chapterChunkQueryIdx = Math.floor(chapterIdx / CHAPTER_CHUNK_SIZE);
      const chapterChunkQuery = assertDefined(queries[chapterChunkQueryIdx]);

      const chapterChunkQueryData = chapterChunkData ?? chapterChunkQuery.data;

      const chapterData =
        chapterChunkQueryData?.data[chapterIdx % CHAPTER_CHUNK_SIZE];

      if (chapterData) {
        const paragraphIdx = chapterData.paragraphs.findIndex((paragraph) =>
          paragraph.sentences.some((sentence) =>
            sentence.words.some((word) => word.chunkId === ttsId),
          ),
        );
        if (paragraphIdx === -1) return null;

        const scrollableParagraphIdx =
          assertDefined(paragraphCountsPrefixSums[chapterIdx]) +
          1 +
          paragraphIdx;

        return {
          chapterChunkQueryIdx,
          index: scrollableParagraphIdx,
          props: getScrollableItemProps(
            scrollableParagraphIdx,
          ) as ParagraphRenderProps,
        };
      }

      const chapterHeaderScrollableIndex = assertDefined(
        paragraphCountsPrefixSums[chapterIdx],
      );
      return {
        chapterChunkQueryIdx,
        index: chapterHeaderScrollableIndex,
        props: getScrollableItemProps(
          chapterHeaderScrollableIndex,
        ) as ChapterHeaderRenderProps,
      };
    },
    [
      documentChapterSummaries,
      getScrollableItemProps,
      paragraphCountsPrefixSums,
      queries,
    ],
  );

  const findScrollableIndexOfChapter = useCallback(
    (chapterId: string) => {
      const chapterIdx = documentChapterSummaries.findIndex(
        (summary) => summary.id === chapterId,
      );
      if (chapterIdx === -1)
        throw new Error(`ChapterId ${chapterId} not found`);

      return {
        index: assertDefined(paragraphCountsPrefixSums[chapterIdx]),
        props: getScrollableItemProps(
          assertDefined(paragraphCountsPrefixSums[chapterIdx]),
        ) as ChapterHeaderRenderProps,
      };
    },
    [
      documentChapterSummaries,
      getScrollableItemProps,
      paragraphCountsPrefixSums,
    ],
  );

  return {
    scrollableItemsCount,
    getScrollableItemProps,
    findScrollableIndexOfTtsChunkId,
    findScrollableIndexOfChapter,
  };
};

// We must define this as a separate hook from the chapterUnfoldingAndQueryLogic because
// we need to call scrollToIndex which is created by the virtualizer (at least for tanstack)
// and the virtualizer needs the chapterUnfoldingAndQueryLogic to be setup.
export const useReaderCommonWithScrollFns = ({
  readerCommon,
  ref,
  scrollToIndex,
  scrollToStart,
  follow,
}: {
  readerCommon: ReturnType<typeof useReaderCommon>;
  ref: RefObject<DocReaderViewHandle | null> | undefined;
  scrollToIndex: (index: number) => void;
  scrollToStart: () => void;
  /** Shared read-along follow session (stable controller-bound methods). */
  follow: { engage: () => void; disengage: () => void };
}) => {
  // Scroll to chunk
  const { logger } = useContextAndErrorIfNull(DocReaderContext);

  const { findScrollableIndexOfTtsChunkId } = readerCommon.scrollableObjects;
  const registry = use(RegistryContext);
  const { chapterChunksFetchParams } = readerCommon.core;
  const scrollToChunk = useCallback(
    (chunkId: string) => {
      const scrollableItem = findScrollableIndexOfTtsChunkId(chunkId);
      if (!scrollableItem) {
        logger.warn({ chunkId }, 'TtsChunkId not found in chapter summaries');
        return;
      }
      if (scrollableItem.props.type !== 'chapter-header') {
        scrollToIndex(scrollableItem.index);
        return;
      }
      logger.info(
        { scrollableItem },
        'Chunk scroll cache miss. Scrolling to chapter header',
      );
      void timeoutPromise(
        loadLegacyChapterChunk(
          registry,
          assertDefined(
            chapterChunksFetchParams[scrollableItem.chapterChunkQueryIdx],
          ),
        ),
        SCROLL_TO_UNLOADED_CHUNK_TIMEOUT_MS,
      )
        .then((chapterData) => {
          const scrollableItem = findScrollableIndexOfTtsChunkId(
            chunkId,
            chapterData,
          );
          if (
            !scrollableItem ||
            scrollableItem.props.type === 'chapter-header'
          ) {
            logger.warn({ chunkId }, 'Expected paragraph after chunk load');
            return;
          }
          scrollToIndex(scrollableItem.index);
        })
        .catch((error: unknown) => {
          logger.warn(
            {
              cause: error instanceof Error ? error : new Error(String(error)),
              metadata: {
                timeout: SCROLL_TO_UNLOADED_CHUNK_TIMEOUT_MS,
                chunkId,
              },
            },
            'Failed to scroll to unloaded chapter chunk within timeout',
          );
        });
    },
    [
      chapterChunksFetchParams,
      findScrollableIndexOfTtsChunkId,
      logger,
      registry,
      scrollToIndex,
    ],
  );

  // Setup Imperative Handle. Navigation scrolls disengage the follow session
  // (the user is deliberately looking elsewhere); jump-to-current re-engages.
  const { scrollableItemsCount, findScrollableIndexOfChapter } =
    readerCommon.scrollableObjects;
  const { engage, disengage } = follow;
  useImperativeHandle(
    ref,
    () => ({
      scrollToIndex: (index: number) => {
        disengage();
        scrollToIndex(index);
      },
      scrollToTextChunk: (chunkId: string) => {
        disengage();
        scrollToChunk(chunkId);
      },
      scrollToTop: () => {
        disengage();
        scrollToStart();
      },
      scrollToBottom: () => {
        disengage();
        scrollToIndex(scrollableItemsCount - 1);
      },
      scrollToChapter: (chapterId: string) => {
        const scrollableItem = findScrollableIndexOfChapter(chapterId);
        disengage();
        scrollToIndex(scrollableItem.index);
      },
      scrollToCurrent: () => {
        engage();
      },
    }),
    [
      disengage,
      engage,
      findScrollableIndexOfChapter,
      scrollToChunk,
      scrollToIndex,
      scrollToStart,
      scrollableItemsCount,
    ],
  );

  return { scrollToChunk };
};
